// =============================================================================
// Modules Sidebar - Barre verticale de navigation inter-modules Lapyno
// Modules disponibles: Projets, Organisation (Admin)
// Note: Superadmin est un espace séparé avec son propre layout
// =============================================================================

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSidebarProjectsStore } from '@/modules/project/hooks';
import lapynoBlue from '@/assets/lapyno-hero-blue.png';
import lapynoGreen from '@/assets/lapyno-hero-green.png';
import lapynoYellow from '@/assets/lapyno-hero-yellow.png';
import lapynoRed from '@/assets/lapyno-hero-red.png';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { 
  FolderKanban, 
  Building2, 
  UserPlus, 
  Sparkles, 
  ArrowLeftRight,
  LayoutDashboard,
  CalendarDays,
  Users,
  FileText,
  MessageCircle,
  ChevronsUp,
  BookOpen,
  Grid3x3,
  Lock,
  Clock,
  Trophy,
  Globe,
  ClipboardList,
  Film,
  Blocks,
  Settings
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useSuperadminCheck } from '@/hooks/useSuperadminCheck';
import { OrganizationSwitcherDialog } from './OrganizationSwitcherDialog';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { InviteDialog } from '@/components/admin/InviteDialog';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useMembersStore } from '@/modules/core/hooks/useMembersStore';
import { useChatStore } from '@/modules/chat/hooks/useChatStore';
import { useAccessControlStore } from '@/modules/core/hooks/useAccessControlStore';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import lapynoLogo from '@/assets/logo-lapyno-no-bg.png';
import aiIcon from '@/assets/icone-ai.png';
import { DashboardIcon } from '@/components/icons/DashboardIcon';
import { ProjectIcon } from '@/components/icons/ProjectIcon';
import { PlanningIcon } from '@/components/icons/PlanningIcon';
import { TeamIcon } from '@/components/icons/TeamIcon';
import { DocumentsIcon } from '@/components/icons/DocumentsIcon';

// AI Icon component wrapper
const AIIconComponent = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <img src={aiIcon} alt="IA" className={className} style={{ ...style, objectFit: 'contain' }} />
);

// Dashboard Icon component wrapper
const DashboardIconComponent = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <DashboardIcon className={className} style={{ ...style, color: '#FFFFFF' }} />
);

// Project Icon component wrapper
const ProjectIconComponent = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <ProjectIcon className={className} style={{ ...style, color: '#FFFFFF' }} />
);

// Planning Icon component wrapper
const PlanningIconComponent = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <PlanningIcon className={className} style={{ ...style, color: '#FFFFFF' }} />
);

// Team Icon component wrapper
const TeamIconComponent = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <TeamIcon className={className} style={{ ...style, color: '#FFFFFF' }} />
);

// Documents Icon component wrapper
const DocumentsIconComponent = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <DocumentsIcon className={className} style={{ ...style, color: '#FFFFFF' }} />
);

// Module button height + gap
const MODULE_BUTTON_SIZE = 36; // h-9 = 36px
const MODULE_GAP = 4; // gap-1 = 4px
const MORE_BUTTON_SIZE = 40; // Extra space for more button

type ModuleType = 'dashboard' | 'projects' | 'planning' | 'ai' | 'teams' | 'documents' | 'wiki' | 'discussion' | 'admin';

const LAST_PROJECT_KEY = 'lapyno_last_project_id';

interface ModuleItem {
  id: ModuleType;
  icon: React.ElementType;
  label: string;
  getPath: () => string;
  color: string;
  comingSoon?: boolean;
  isImageIcon?: boolean;
}

// Determine active module from pathname
function getActiveModule(pathname: string): ModuleType {
  if (pathname.startsWith('/dashboard')) {
    return 'dashboard';
  }
  const adminRoutes = ['/admin', '/permissions-guide'];
  if (adminRoutes.some(route => pathname.startsWith(route))) {
    return 'admin';
  }
  if (pathname.startsWith('/teams')) {
    return 'teams';
  }
  if (pathname.startsWith('/planning')) {
    return 'planning';
  }
  if (pathname.startsWith('/discussion')) {
    return 'discussion';
  }
  if (pathname.startsWith('/wiki')) {
    return 'wiki';
  }
  if (pathname.startsWith('/documents')) {
    return 'documents';
  }
  if (pathname.startsWith('/ai')) {
    return 'ai';
  }
  return 'projects';
}

// Get the projects module entry point
function getProjectsPath(): string {
  return '/projects';
}

const MODULES: ModuleItem[] = [
  {
    id: 'dashboard',
    icon: DashboardIconComponent,
    label: 'Tableau de bord',
    getPath: () => '/dashboard',
    color: '#10B981', // Emerald
  },
  {
    id: 'projects',
    icon: ProjectIconComponent,
    label: 'Projets',
    getPath: getProjectsPath,
    color: '#3B82F6', // Blue
  },
  {
    id: 'planning',
    icon: PlanningIconComponent,
    label: 'Planification',
    getPath: () => '/planning',
    color: '#0D9488', // Teal
  },
  {
    id: 'ai',
    icon: AIIconComponent,
    label: 'IA',
    getPath: () => '/ai',
    color: '#8B5CF6', // Violet
    isImageIcon: true,
  },
  {
    id: 'teams',
    icon: TeamIconComponent,
    label: 'Équipes',
    getPath: () => '/teams',
    color: '#EC4899', // Pink
  },
  {
    id: 'documents',
    icon: DocumentsIconComponent,
    label: 'Documents',
    getPath: () => '/documents',
    color: '#06B6D4', // Cyan
  },
  {
    id: 'wiki',
    icon: BookOpen,
    label: 'Wiki',
    getPath: () => '/wiki',
    color: '#6366F1', // Indigo
  },
  {
    id: 'discussion',
    icon: MessageCircle,
    label: 'Discussion',
    getPath: () => '/discussion',
    color: '#EF4444', // Red
  },
];

// Pick a mascot image based on module color
const MASCOT_MAP: Record<string, string> = {
  '#10B981': lapynoGreen,
  '#8B5CF6': lapynoBlue,
  '#3B82F6': lapynoBlue,
  '#F59E0B': lapynoYellow,
  '#EF4444': lapynoRed,
};
function getMascotForColor(color: string) {
  return MASCOT_MAP[color] || lapynoBlue;
}

export function ModulesSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const activeModule = getActiveModule(location.pathname);
  const { isSuperadmin } = useSuperadminCheck();
  const { currentOrganization } = useOrganization();
  const { currentUserRole } = useAccessControlStore(currentOrganization?.id || null);
  const canAccessAdmin = currentUserRole === 'owner' || currentUserRole === 'admin';
  const { invite } = useMembersStore(currentOrganization?.id);
  const chatUnreadCounts = useChatStore(s => s.unreadCounts);
  const chatInitMemberId = useChatStore(s => s.initMemberId);
  const chatLoadUnreadCounts = useChatStore(s => s.loadUnreadCounts);
  const chatSubscribeRealtime = useChatStore(s => s.subscribeRealtime);
  const totalChatUnread = useMemo(() => 
    Object.values(chatUnreadCounts).reduce((sum: number, c: number) => sum + c, 0),
    [chatUnreadCounts]
  );

  // Load chat unread counts AND subscribe to realtime globally
  // so badges update and toasts fire even outside the Discussion module
  useEffect(() => {
    if (!currentOrganization?.id) return;
    const orgId = currentOrganization.id;
    let unsub: (() => void) | undefined;
    chatInitMemberId(orgId).then(() => {
      chatLoadUnreadCounts(orgId);
      unsub = chatSubscribeRealtime(orgId);
    });
    return () => unsub?.();
  }, [currentOrganization?.id]);
  const [showOrgSwitcher, setShowOrgSwitcher] = useState(false);
  const [showMoreModules, setShowMoreModules] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [comingSoonModal, setComingSoonModal] = useState<{ open: boolean; module: ModuleItem | null }>({
    open: false,
    module: null,
  });

  // Dynamic calculation of visible modules based on available space
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(MODULES.length);

  const calculateVisibleModules = useCallback(() => {
    if (!containerRef.current) return;
    
    const containerHeight = containerRef.current.clientHeight;
    const paddingY = 24; // py-3 = 12px * 2
    // Always reserve space for the "Plus" button + label (~52px) and a minimum gap
    const reservedForPlus = MORE_BUTTON_SIZE + 16 + 12; // button + label + gap
    const availableHeight = containerHeight - paddingY - reservedForPlus;
    
    // Calculate how many modules can fit
    let count = 0;
    let usedHeight = 0;
    
    for (let i = 0; i < MODULES.length; i++) {
      const moduleHeight = MODULE_BUTTON_SIZE + (i > 0 ? MODULE_GAP : 0);
      
      if (usedHeight + moduleHeight <= availableHeight) {
        usedHeight += moduleHeight;
        count++;
      } else {
        break;
      }
    }
    
    // Ensure at least 1 module is visible
    setVisibleCount(Math.max(1, Math.min(count, MODULES.length)));
  }, []);

  useEffect(() => {
    calculateVisibleModules();
    
    const resizeObserver = new ResizeObserver(() => {
      calculateVisibleModules();
    });
    
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    
    window.addEventListener('resize', calculateVisibleModules);
    
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', calculateVisibleModules);
    };
  }, [calculateVisibleModules]);

  // Split modules into visible and hidden based on calculated count
  const visibleModules = MODULES.slice(0, visibleCount);
  const hiddenModules = MODULES.slice(visibleCount);
  const plusActiveModules: Record<string, string> = {};
  const hasHiddenActiveModule = hiddenModules.some(m => m.id === activeModule);
  const plusActiveColor = hiddenModules.find(m => m.id === activeModule)?.color || '#64748B';
  const showMoreButton = true;

  // Store last opened project ID in localStorage
  useEffect(() => {
    const match = location.pathname.match(/^\/projects\/([a-f0-9-]+)/);
    if (match) {
      localStorage.setItem(LAST_PROJECT_KEY, match[1]);
    }
  }, [location.pathname]);

  const handleModuleClick = async (module: ModuleItem, closePopover?: boolean) => {
    if (closePopover) {
      setShowMoreModules(false);
    }
    if (module.comingSoon) {
      setComingSoonModal({ open: true, module });
    } else {
      const path = module.getPath();
      // If navigating to /app (no last project), try to find first project from store
      if (path === '/app' && module.id === 'projects') {
        let sidebarProjects = useSidebarProjectsStore.getState().projects;
        // If store is empty, load projects first
        if (sidebarProjects.length === 0 && currentOrganization?.id) {
          sidebarProjects = await useSidebarProjectsStore.getState().loadProjects(currentOrganization.id);
        }
        const activeProject = sidebarProjects.find(p => p.status === 'active');
        if (activeProject) {
          localStorage.setItem(LAST_PROJECT_KEY, activeProject.id);
          navigate(`/projects/${activeProject.id}`);
          return;
        }
      }
      navigate(path);
    }
  };

  const getActiveModuleIndex = () => {
    const index = MODULES.findIndex(m => m.id === activeModule);
    return index >= 0 ? index : 1; // Default to projects (index 1)
  };

  return (
    <>
      <aside 
        data-tour="modules-sidebar"
        className="hidden md:flex flex-col w-14 h-full overflow-hidden rounded-xl border"
        style={{ 
          background: '#6dafed',
          borderColor: 'rgba(255,255,255,0.15)',
        }}
      >
        {/* Logo Section */}
        <div 
          className="h-12 shrink-0 flex items-center justify-center cursor-pointer"
          onClick={() => navigate('/app')}
        >
          <img 
            src={lapynoLogo} 
            alt="Lapyno" 
            className="w-[22px] h-[22px] rounded hover:scale-105 transition-transform object-cover"
          />
        </div>

        {/* Module Icons - Visible modules */}
        <div ref={containerRef} className="flex-1 flex flex-col items-center py-3 gap-1">
          {visibleModules.map((module) => {
            if (!module?.icon) return null;
            const Icon = module.icon;
            const isActive = activeModule === module.id;

            return (
              <div key={module.id} className="relative" data-tour={`module-${module.id}`}>
                {/* Active indicator - positioned relative to each button */}
                {isActive && (
                  <div 
                    className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-[7px] w-1 h-6 rounded-r-full"
                    style={{ backgroundColor: module.color }}
                  />
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => handleModuleClick(module)}
                      className={cn(
                        "w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-200",
                        isActive 
                          ? "scale-105" 
                          : "hover:scale-105"
                      )}
                      style={{
                        backgroundColor: isActive ? `${module.color}20` : 'transparent',
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.10)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <Icon 
                        className="h-[18px] w-[18px] transition-colors" 
                        style={{ 
                          color: isActive ? '#1E3A5F' : '#FFFFFF',
                          strokeWidth: isActive ? 2.5 : 1.75
                        }} 
                      />
                      {module.id === 'discussion' && totalChatUnread > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center text-white"
                          style={{ backgroundColor: '#EF4444' }}>
                          {totalChatUnread > 99 ? '99+' : totalChatUnread}
                        </span>
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent 
                    side="right" 
                    sideOffset={8}
                    style={{ 
                      backgroundColor: '#1E293B', 
                      color: '#FFFFFF', 
                      borderColor: 'rgba(255,255,255,0.25)' 
                    }}
                  >
                    <span>{module.label}</span>
                    {module.comingSoon && (
                      <span className="ml-2 text-xs opacity-60">(Bientôt)</span>
                    )}
                  </TooltipContent>
                </Tooltip>
              </div>
            );
          })}

          {/* More modules button - only shown when space is insufficient */}
          {showMoreButton && (
            <div className="relative flex flex-col items-center">
              {/* Active indicator if hidden module is active */}
              {hasHiddenActiveModule && (
                <div 
                  className="absolute left-0 top-[18px] -translate-y-1/2 -translate-x-[7px] w-1 h-6 rounded-r-full"
                  style={{ backgroundColor: plusActiveColor }}
                />
              )}
              <Popover open={showMoreModules} onOpenChange={setShowMoreModules}>
                <PopoverTrigger asChild>
                  <button
                    className={cn(
                      "relative w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-105",
                      hasHiddenActiveModule && "scale-105"
                    )}
                    style={{
                      backgroundColor: hasHiddenActiveModule 
                        ? `${plusActiveColor}20`
                        : 'transparent',
                    }}
                    onMouseEnter={(e) => {
                       if (!hasHiddenActiveModule) {
                         e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.10)';
                       }
                    }}
                    onMouseLeave={(e) => {
                      if (!hasHiddenActiveModule) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <Grid3x3 
                      className="h-[18px] w-[18px] transition-colors" 
                      style={{ 
                        color: hasHiddenActiveModule 
                          ? hiddenModules.find(m => m.id === activeModule)?.color 
                          : '#FFFFFF',
                         strokeWidth: hasHiddenActiveModule ? 2.5 : 1.75
                      }} 
                    />
                    {totalChatUnread > 0 && hiddenModules.some(m => m.id === 'discussion') && activeModule !== 'discussion' && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center text-white"
                        style={{ backgroundColor: '#EF4444' }}>
                        {totalChatUnread > 99 ? '99+' : totalChatUnread}
                      </span>
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent 
                  side="right" 
                  sideOffset={12}
                  className="w-[300px] p-0 rounded-2xl overflow-hidden"
                  style={{ 
                    backgroundColor: '#FFFFFF', 
                    border: '2px solid #CBD5E1',
                    boxShadow: '0 12px 40px -10px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.04)'
                  }}
                >
                  <div className="p-4">
                    {/* All modules in one continuous grid — ClickUp style */}
                    <div className="grid grid-cols-3 gap-2">
                      {/* Hidden (overflow) modules */}
                      {hiddenModules.map((module) => {
                        if (!module?.icon) return null;
                        const Icon = module.icon;
                        const isActive = activeModule === module.id;

                        return (
                          <button
                            data-tour={`module-${module.id}`}
                            key={module.id}
                            onClick={() => handleModuleClick(module, true)}
                            className={cn(
                              "flex flex-col items-center gap-2 px-2 py-3 rounded-2xl transition-all text-center",
                              isActive 
                                ? "font-medium" 
                                : "hover:bg-slate-50"
                            )}
                            style={{
                              backgroundColor: isActive ? `${module.color}08` : 'transparent',
                            }}
                          >
                            <div 
                              className="relative w-9 h-9 rounded-xl flex items-center justify-center"
                              style={{ 
                                background: `linear-gradient(135deg, ${module.color}18 0%, ${module.color}08 100%)`,
                                border: `1.5px solid ${module.color}20`,
                              }}
                            >
                              <Icon 
                                className="h-[18px] w-[18px]" 
                                style={{ color: module.color, strokeWidth: 2 }} 
                              />
                              {module.id === 'discussion' && totalChatUnread > 0 && (
                                <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center text-white"
                                  style={{ backgroundColor: '#EF4444' }}>
                                  {totalChatUnread > 99 ? '99+' : totalChatUnread}
                                </span>
                              )}
                            </div>
                            <span 
                              className="text-[11px] leading-tight truncate w-full font-medium"
                              style={{ color: isActive ? module.color : '#475569' }}
                            >
                              {module.label}
                            </span>
                          </button>
                        );
                      })}



                      {[
                        { icon: Globe, label: 'Espaces', color: '#8B5CF6', bg: '#EDE9FE' },
                        { icon: ClipboardList, label: 'Formulaires', color: '#0EA5E9', bg: '#E0F2FE' },
                        { icon: Trophy, label: 'Objectifs', color: '#D97706', bg: '#FEF3C7' },
                        { icon: Film, label: 'Clips', color: '#EF4444', bg: '#FEE2E2' },
                        { icon: Blocks, label: 'Applications', color: '#6366F1', bg: '#E0E7FF' },
                        { icon: Clock, label: 'Relevés de ...', color: '#F97316', bg: '#FFEDD5' },
                      ].map((item) => (
                        <button
                          key={item.label}
                          onClick={() => {
                            setShowMoreModules(false);
                            setComingSoonModal({ 
                              open: true, 
                              module: { 
                                id: 'dashboard' as ModuleType, 
                                icon: item.icon, 
                                label: item.label, 
                                getPath: () => '/', 
                                color: item.color, 
                                comingSoon: true 
                              } 
                            });
                          }}
                          className="flex flex-col items-center gap-2 px-2 py-3 rounded-2xl transition-all text-center hover:bg-slate-50 relative"
                        >
                          <div 
                            className="w-9 h-9 rounded-xl flex items-center justify-center relative"
                            style={{ 
                              background: `linear-gradient(135deg, ${item.bg} 0%, ${item.bg}80 100%)`,
                              border: `1.5px solid ${item.color}15`,
                            }}
                          >
                            <item.icon 
                              className="h-[18px] w-[18px] opacity-60" 
                              style={{ color: item.color, strokeWidth: 2 }} 
                            />
                            <div 
                              className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center shadow-sm"
                              style={{ backgroundColor: '#F1F5F9', border: '1.5px solid #E2E8F0' }}
                            >
                              <Lock className="h-2.5 w-2.5 text-slate-400" />
                            </div>
                          </div>
                          <span className="text-[11px] leading-tight text-slate-400 truncate w-full font-medium">
                            {item.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Personnaliser la navigation button */}
                  <div className="border-t border-slate-100 px-4 py-3">
                    <button 
                      className="flex items-center justify-center gap-2 w-full text-[12px] text-slate-800 hover:bg-slate-200 transition-colors rounded-xl px-2.5 py-2.5 border-2 border-slate-300 shadow-sm"
                      onClick={() => {
                        setShowMoreModules(false);
                        toast.info('Bientôt disponible', {
                          description: 'La personnalisation de la navigation sera disponible prochainement.'
                        });
                      }}
                    >
                      <Settings className="h-4 w-4 text-slate-700" />
                      <span className="font-medium">Personnaliser la navigation</span>
                    </button>
                  </div>
                </PopoverContent>
              </Popover>
              <span className="text-[9px] mt-0.5 font-bold" style={{ color: '#FFFFFF' }}>Plus</span>
            </div>
          )}
        </div>


        {/* Upgrade Button */}
        <div className="shrink-0 flex flex-col items-center py-2 border-t" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button 
                className="w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-300 group relative overflow-hidden" 
                style={{
                  background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 50%, #0EA5E9 100%)',
                  boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)'
                }} 
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'scale(1.08)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(139, 92, 246, 0.5)';
                }} 
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(139, 92, 246, 0.3)';
                }} 
                onClick={() => {
                  toast.info('Bientôt disponible', {
                    description: 'La page des forfaits sera disponible prochainement.'
                  });
                }}
              >
                <ChevronsUp 
                  className="h-[18px] w-[18px] relative z-10" 
                  style={{ color: '#FFFFFF', strokeWidth: 2.5 }} 
                />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={8} style={{ backgroundColor: '#1E293B', color: '#FFFFFF', borderColor: 'rgba(255,255,255,0.25)' }}>
              Mettre à niveau
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Bottom Action Buttons */}
        <div className="shrink-0 flex flex-col items-center py-2 gap-1 border-t" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
          {/* Organisation Module - Only visible for owner/admin */}
          {canAccessAdmin && (
            <div className="relative" data-tour="module-admin">
              {activeModule === 'admin' && (
                <div 
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-[7px] w-1 h-6 rounded-r-full"
                  style={{ backgroundColor: '#DC2626' }}
                />
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => navigate('/admin/access')}
                    className={cn(
                      "w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-200",
                      activeModule === 'admin' ? "scale-105" : "hover:scale-105"
                    )}
                    style={{
                      backgroundColor: activeModule === 'admin' ? '#DC262620' : 'transparent',
                    }}
                    onMouseEnter={(e) => { if (activeModule !== 'admin') e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.10)'; }}
                    onMouseLeave={(e) => { if (activeModule !== 'admin') e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <Building2 
                      className="h-[18px] w-[18px] transition-colors" 
                      style={{ 
                        color: activeModule === 'admin' ? '#DC2626' : '#FFFFFF',
                        strokeWidth: activeModule === 'admin' ? 2.5 : 2
                      }} 
                    />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={8} style={{ backgroundColor: '#1E293B', color: '#FFFFFF', borderColor: 'rgba(255,255,255,0.25)' }}>
                  Workspace
                </TooltipContent>
              </Tooltip>
            </div>
          )}

          {/* Invite User - Only visible for owner/admin */}
          {canAccessAdmin && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  data-tour="invite-member"
                  onClick={() => setShowInviteDialog(true)}
                  className="w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-105"
                  style={{ backgroundColor: 'transparent' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.10)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <UserPlus className="h-[18px] w-[18px]" style={{ color: '#FFFFFF' }} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={8} style={{ backgroundColor: '#1E293B', color: '#FFFFFF', borderColor: 'rgba(255,255,255,0.25)' }}>
                Inviter une personne
              </TooltipContent>
            </Tooltip>
          )}

          {/* Change Organization */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => setShowOrgSwitcher(true)}
                className="w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-105"
                style={{ backgroundColor: 'transparent' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.10)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <ArrowLeftRight className="h-[18px] w-[18px]" style={{ color: '#FFFFFF' }} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={8} style={{ backgroundColor: '#1E293B', color: '#FFFFFF', borderColor: 'rgba(255,255,255,0.25)' }}>
              Changer de workspace
            </TooltipContent>
          </Tooltip>
        </div>
      </aside>

      {/* Organization Switcher Dialog */}
      <OrganizationSwitcherDialog
        open={showOrgSwitcher}
        onOpenChange={setShowOrgSwitcher}
      />

      {/* ClickUp-style Invite Dialog — accessible depuis la sidebar globale */}
      <InviteDialog
        open={showInviteDialog}
        onOpenChange={setShowInviteDialog}
        onInvite={async (email, role) => {
          const result = await invite(email, role);
          if (result.success) {
            toast.success('Invitation envoyée avec succès !');
          }
          return result;
        }}
        onGoToMembers={() => {
          setShowInviteDialog(false);
          navigate('/admin/access?tab=members');
        }}
      />

      <Dialog 
        open={comingSoonModal.open} 
        onOpenChange={(open) => setComingSoonModal({ open, module: open ? comingSoonModal.module : null })}
      >
        <DialogContent 
          className="sm:max-w-md border-0 overflow-hidden p-0"
          style={{ backgroundColor: '#0F172A' }}
        >
          {comingSoonModal.module && (
            <>
              {/* Header with gradient */}
              <div 
                className="relative px-6 pt-8 pb-6 text-center"
                style={{ 
                  background: `linear-gradient(135deg, ${comingSoonModal.module.color}40 0%, ${comingSoonModal.module.color}10 100%)` 
                }}
              >
                {/* Floating particles */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  {[...Array(6)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute w-2 h-2 rounded-full opacity-30"
                      style={{
                        backgroundColor: comingSoonModal.module?.color,
                        left: `${15 + i * 15}%`,
                        top: `${20 + (i % 3) * 25}%`,
                        animation: `float ${2 + i * 0.3}s ease-in-out infinite`,
                        animationDelay: `${i * 0.2}s`,
                      }}
                    />
                  ))}
                </div>

                {/* Rabbit Mascot */}
                <div className="flex justify-center mb-4">
                  <div 
                    className="relative"
                    style={{ 
                      filter: `drop-shadow(0 4px 20px ${comingSoonModal.module.color}40)`,
                      animation: 'bounce 2s ease-in-out infinite'
                    }}
                  >
                    <img src={getMascotForColor(comingSoonModal.module.color)} alt="Lapyno" className="w-24 h-24 object-contain" />
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-white mb-2">
                  {comingSoonModal.module.label}
                </h2>
                <p className="text-sm text-slate-400">
                  Bientôt disponible sur Lapyno
                </p>
              </div>

              {/* Content */}
              <div className="px-6 py-6">
                <div 
                  className="rounded-xl p-4 mb-6"
                  style={{ backgroundColor: '#1E293B' }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${comingSoonModal.module.color}20` }}
                    >
                      <comingSoonModal.module.icon 
                        className="h-5 w-5" 
                        style={{ color: comingSoonModal.module.color }} 
                      />
                    </div>
                    <div>
                      <h3 className="font-medium text-white">
                        Fonctionnalité en développement
                      </h3>
                      <p className="text-xs text-slate-400">
                        Notre équipe travaille dessus 🐰
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-300 leading-relaxed">
                    Cette fonctionnalité sera bientôt disponible ! Nous travaillons 
                    activement pour vous offrir la meilleure expérience possible.
                  </p>
                </div>

                <Button
                  className="w-full h-11 font-medium text-white"
                  style={{ 
                    background: `linear-gradient(135deg, ${comingSoonModal.module.color} 0%, ${comingSoonModal.module.color}CC 100%)`,
                    boxShadow: `0 4px 14px ${comingSoonModal.module.color}40`
                  }}
                  onClick={() => setComingSoonModal({ open: false, module: null })}
                >
                  J'ai compris !
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Animation keyframes */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-10px) rotate(5deg); }
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
      `}</style>
    </>
  );
}
